import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface NavBarProps {
  onMenuToggle?: () => void;
}

export function NavBar({ onMenuToggle }: NavBarProps) {
  const { user, userAttributes, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Prefer email from attributes, fall back to signInDetails, then username
  // Cognito sub (UUID) shows as username - detect and replace with sign-in ID
  const rawUsername = user?.username || '';
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(rawUsername);
  const email = userAttributes?.email
    || user?.signInDetails?.loginId
    || (isUuid ? 'User' : rawUsername)
    || 'User';
  const initials = email.includes('@')
    ? email.split('@')[0].slice(0, 2).toUpperCase()
    : email.slice(0, 2).toUpperCase();

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <nav className="navbar">
      <button className="navbar__hamburger" onClick={onMenuToggle} type="button" aria-label="Toggle menu">
        <span /><span /><span />
      </button>
      <div className="navbar__brand">
        <img src="/admin/flux-logo-small.png" alt="Flux" className="navbar__brand-img" />
        <span className="navbar__title">
          <span className="navbar__title--bold">Flux</span>
        </span>
      </div>

      <div className="navbar__spacer" />

      <div className="navbar__user" ref={menuRef}>
        <button
          className="navbar__user-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          type="button"
        >
          <span className="navbar__avatar">{initials}</span>
          <span className="navbar__email">{email}</span>
          <span className="navbar__caret">{menuOpen ? '\u25B4' : '\u25BE'}</span>
        </button>

        {menuOpen && (
          <div className="navbar__dropdown">
            <div className="navbar__dropdown-header">{email}</div>
            <button
              className="navbar__dropdown-item"
              onClick={() => { setMenuOpen(false); navigate('/admin/me'); }}
              type="button"
            >
              Profile
            </button>
            <button
              className="navbar__dropdown-item"
              onClick={() => { setMenuOpen(false); signOut(); }}
              type="button"
            >
              Sign Out
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
